// react
import { FC, useState } from 'react';
import { AxiosError, AxiosInstance } from 'axios';
import { AiOutlineEyeInvisible } from 'react-icons/ai';
import { MdOutlineVisibility } from 'react-icons/md';

// components
import { FormInput } from '../Form/FormInput';
import { MainCustomButton } from '../Buttons';

// hooks
import { useTranslation } from 'react-i18next';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../store/auth';

// imports
import { Link } from 'react-router-dom';
import { loginValidationSchema } from '../../validation/schemas';
import { AuthService } from '../../services/AuthService';
import { errorToast } from '../../utils/toasts';
import { useApi } from '../../services/hooks/apiHook';
import { errorMessageAdapter } from '../../adapters';
import { OFFSHORE_FLOW_SESSION_KEY } from '../../constants/offshoreLpIntent';

interface authInfos {
  email: string;
  password: string;
}

const LoginForm: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const resolver = yupResolver(loginValidationSchema);
  const form = useForm<authInfos>({ resolver, mode: 'onChange' });

  const { t } = useTranslation();
  const { control, handleSubmit, formState } = form;
  const { errors } = formState;

  const { setToken } = useAuth();
  const api: AxiosInstance = useApi();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmitForm: SubmitHandler<authInfos> = async (data) => {
    try {
      setLoading(true);
      const { response } = await AuthService.login(api, data);
      setLoading(false);
      // Stale LP intent must not divert this login to /choose-onboarding.
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(OFFSHORE_FLOW_SESSION_KEY);
      }
      setToken(response.access_token);
    } catch (e: unknown) {
      setLoading(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
  };

  return (
    <form id="MainForm" className="flex w-full flex-col items-start justify-center gap-5" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className="flex w-full flex-col gap-5">
        <FormInput
          id="email"
          name="email"
          label={t('labels.lblEmail')}
          type="email"
          error={t(errors.email?.message || '')}
          control={control}
        />
        <div className="relative">
          <FormInput
            id="password"
            name="password"
            label={t('labels.lblPassword')}
            type={showPassword ? 'text' : 'password'}
            error={t(errors.password?.message || '')}
            control={control}
          />
          <button
            type="button"
            className="absolute bottom-2 right-0 flex items-center pr-3 text-sm leading-5"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? <MdOutlineVisibility fontSize={25} /> : <AiOutlineEyeInvisible fontSize={25} />}
          </button>
        </div>
        <Link to="/forgot-password" className="self-end text-right text-sm font-bold text-slate-700 underline">
          {t('others.othForgotPwd')}
        </Link>
      </div>

      <MainCustomButton text={t('buttons.btnLogin')} form="MainForm" loading={loading} className="!max-w-none" />

      <Link to={'/register'} className="text-sm text-slate-600">
        {t('others.othNoAccount')} <span className="font-bold text-slate-700 underline">{t('buttons.btnRegister')}</span>
      </Link>
      <a href="https://offshore2go.com/" className="text-xs text-slate-500 underline">
        {t('labels.lblBackToHomePage')}
      </a>
    </form>
  );
};

export { LoginForm };
